import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeIn } from "../FadeIn";
import { useT } from "../../lib/i18n";
import { CULINAIRES } from "../../lib/images";

export const CulinairesSection = () => {
  const t = useT();
  const [feature, ...rest] = CULINAIRES;
  const total = String(CULINAIRES.length).padStart(2, "0");

  return (
    <section
      className="bg-ink text-bone py-20 md:py-32 relative overflow-hidden"
      data-testid="culinaires-section"
    >
      {/* Warm gradient accents */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 15% 10%, rgba(184,169,154,0.18) 0%, rgba(0,0,0,0) 50%), radial-gradient(ellipse at 90% 80%, rgba(184,169,154,0.10) 0%, rgba(0,0,0,0) 55%)",
        }}
      />

      <div className="container-x relative">
        {/* Editorial header */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-20 items-end">
          <FadeIn className="md:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-stone/60" />
              <div className="eyebrow text-stone">{t("culinaires.eyebrow")}</div>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[0.95]">
              {t("culinaires.headlineA")}{" "}
              <span className="italic text-stone">{t("culinaires.headlineB")}</span>.
            </h2>
          </FadeIn>
          <FadeIn delay={0.15} className="md:col-span-5">
            <p className="text-bone/65 text-sm md:text-base leading-relaxed max-w-md">
              {t("culinaires.intro")}
            </p>
          </FadeIn>
        </div>

        {/* Mobile: horizontal scroll editorial cards */}
        <div
          className="flex md:hidden -mx-4 px-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 pb-2"
          data-testid="culinaires-scroller"
        >
          {CULINAIRES.map((c, i) => (
            <figure
              key={c.id}
              data-testid={`culinaire-${c.id}`}
              className="relative w-[78vw] aspect-[4/5] flex-shrink-0 snap-start overflow-hidden rounded-sm bg-black"
            >
              <img
                src={c.src}
                alt={c.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
              <div className="absolute top-4 left-4 font-display text-xs tracking-[0.3em] text-bone/70">
                {String(i + 1).padStart(2, "0")} / {total}
              </div>
              <figcaption className="absolute bottom-0 left-0 right-0 p-5 text-bone">
                <div className="eyebrow text-stone mb-2">{t("culinaires.kicker")}</div>
                <div className="font-display text-2xl italic leading-tight">{c.title}</div>
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Desktop: editorial split (large feature + asymmetric grid) */}
        <div className="hidden md:grid grid-cols-12 gap-5 lg:gap-6">
          {/* Feature image (left, tall) */}
          <FadeIn className="col-span-7">
            <figure
              data-testid={`culinaire-md-${feature.id}`}
              data-cursor="VOIR"
              className="group relative aspect-[4/5] overflow-hidden bg-black rounded-sm md:cursor-none"
            >
              <img
                src={feature.src}
                alt={feature.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/15 to-transparent" />
              <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                <div className="font-display text-sm tracking-[0.3em] text-bone/70">
                  01 / {total}
                </div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-stone bg-black/40 backdrop-blur-sm px-3 py-1.5 border border-stone/30">
                  {t("culinaires.feature")}
                </div>
              </div>
              <figcaption className="absolute bottom-0 left-0 right-0 p-8 lg:p-10 text-bone">
                <div className="eyebrow text-stone mb-3">{t("culinaires.kicker")}</div>
                <div className="font-display text-3xl lg:text-5xl italic leading-[0.95] mb-2">
                  {feature.title}
                </div>
                <div className="h-px w-12 bg-stone/60 mt-4 transition-all duration-700 group-hover:w-24" />
              </figcaption>
            </figure>
          </FadeIn>

          {/* Asymmetric grid (right) */}
          <div className="col-span-5 grid grid-cols-2 gap-5 lg:gap-6 auto-rows-fr">
            {rest.slice(0, 4).map((c, idx) => (
              <FadeIn key={c.id} delay={0.1 + idx * 0.08}>
                <figure
                  data-testid={`culinaire-md-${c.id}`}
                  data-cursor="VOIR"
                  className={`group relative overflow-hidden bg-black rounded-sm md:cursor-none ${
                    idx === 1 ? "aspect-[4/5]" : "aspect-square"
                  } ${idx === 2 ? "-mt-12" : ""}`}
                >
                  <img
                    src={c.src}
                    alt={c.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.08]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute top-3 left-3 font-display text-[10px] tracking-[0.3em] text-bone/70">
                    {String(idx + 2).padStart(2, "0")}
                  </div>
                  <figcaption className="absolute bottom-0 left-0 right-0 p-4 lg:p-5 text-bone translate-y-1 opacity-95 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="font-display text-lg lg:text-xl italic leading-tight">
                      {c.title}
                    </div>
                  </figcaption>
                </figure>
              </FadeIn>
            ))}
          </div>
        </div>

        {/* Editorial CTA strip */}
        <FadeIn delay={0.2}>
          <div className="mt-14 md:mt-20 flex items-center justify-between border-t border-bone/10 pt-8">
            <div className="text-[10px] tracking-[0.28em] uppercase text-bone/45">
              Photos · Romain Bruneau
            </div>
            <Link
              to="/portfolio"
              data-testid="culinaires-cta"
              data-cursor="VOIR"
              className="inline-flex items-center gap-3 text-stone hover:gap-5 transition-all"
            >
              <span className="text-[11px] tracking-[0.22em] uppercase">
                {t("culinaires.cta")}
              </span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};
